import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Lock, Mail, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const formatErr = (d) => {
    if (!d) return "Something went wrong";
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((e) => e.msg || JSON.stringify(e)).join(" ");
    return d.msg || JSON.stringify(d);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const data = await login(email, password);
      const defaultLanding = data.role === "admin" ? "/dashboard" : "/dashboard";
      const from = location.state?.from?.pathname || defaultLanding;
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1e2a3a] via-[#16202d] to-[#0e1620] p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex h-14 w-14 items-center justify-center rounded-xl bg-[#226DB4] text-white text-2xl font-bold shadow-lg">
            H
          </div>
          <h1 className="mt-3 text-2xl font-semibold text-white tracking-wide">
            HRMS Admin Console
          </h1>
          <p className="text-slate-400 text-sm mt-1">Sign in to manage employees</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-lg shadow-xl p-8 space-y-5"
          data-testid="login-form"
        >
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full border border-slate-200 rounded-md pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4] focus:border-transparent"
                placeholder="User ID"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full border border-slate-200 rounded-md pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#226DB4] focus:border-transparent"
                placeholder="••••••••"
              />
            </div>
          </div>

          {err && (
            <div
              data-testid="login-error"
              className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm"
            >
              {err}
            </div>
          )}

          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={busy}
            className="w-full bg-[#226DB4] hover:bg-[#1B5A94] disabled:opacity-60 text-white font-medium py-2.5 rounded-md transition flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {busy ? "Signing in…" : "Sign In"}
          </button>

          <div className="text-[11px] text-center text-slate-400 pt-1 space-y-0.5">
            Use your registered credentials to login
          </div>
        </form>
      </div>
    </div>
  );
}
